import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Grid2 as Grid, Card, CardContent, CardActions, CardActionArea,
  Chip, Button, TextField, InputAdornment, Menu, MenuItem, Radio, Checkbox, Avatar, AvatarGroup,
  Skeleton, Alert, IconButton, Tooltip, Tabs, Tab, FormControlLabel, Switch,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  Language as OnlineIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  CalendarMonth as CalendarIcon,
  Groups as GroupsIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { useNewCounts } from 'shared/hooks/useNewCounts';
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader, { HEADER_TABS_SX } from 'components/common/layout/PageHeader';
import RsvpButton from './RsvpButton';
import EventFormDialog from './EventFormDialog';
import { EVENT_TYPES, STATUS_COLORS, connectionsLine, formatWhen, typeColor } from './eventMeta';

const PAGE_SIZE = 48;

// "Relevant to me" is remembered per browser; on by default, as on the Interests page
const RELEVANT_KEY = 'otable.events.relevant';
const readRelevant = () => { try { return localStorage.getItem(RELEVANT_KEY) !== '0'; } catch { return true; } };

const STATUSES = [
  { value: 'current',   label: 'Upcoming & ongoing' },
  { value: 'upcoming',  label: 'Upcoming' },
  { value: 'ongoing',   label: 'Happening now' },
  { value: 'past',      label: 'Past' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: '',          label: 'All' },
];

// What each tab asks the server for
const TABS = [
  { value: 'discover',   label: 'Discover',    params: {} },
  { value: 'going',      label: 'Going',       params: { rsvp: 'going' } },
  { value: 'interested', label: 'Interested',  params: { rsvp: 'interested' } },
  { value: 'invited',    label: 'Invitations', params: { rsvp: 'invited' } },
  { value: 'hosting',    label: 'Hosting',     params: { hosting: 'true' } },
];

// ── EventCard ────────────────────────────────────────────────────────────────
function EventCard({ event, onChange }) {
  const navigate = useNavigate();
  const color = typeColor(event.event_type);
  const restricted = event.visibility !== 'public';
  const group = event.lab_info?.name || event.consortium_info?.name;
  const line = connectionsLine(event);

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
      }}
    >
      {/* Top accent bar */}
      <Box sx={{ height: 4, background: color, borderRadius: '12px 12px 0 0' }} />

      <CardActionArea onClick={() => navigate(`/events/${event.id}/`)} sx={{ flex: 1, alignItems: 'flex-start', display: 'flex' }}>
        <CardContent sx={{ flex: 1, pt: 1.5, pb: 1 }}>
          {/* Type + Status row */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Chip size="small" label={event.event_type_display}
              sx={{ bgcolor: color, color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
            {event.status !== 'upcoming' && (
              <Chip size="small" label={event.status_display} color={STATUS_COLORS[event.status] || 'default'}
                variant="outlined" sx={{ fontSize: '0.7rem', fontWeight: 500 }} />
            )}
            {event.is_online && (
              <Chip size="small" icon={<OnlineIcon sx={{ fontSize: 14 }} />} label="Online" variant="outlined" sx={{ fontSize: '0.7rem' }} />
            )}
            {restricted && (
              <Chip size="small" icon={<LockIcon sx={{ fontSize: 13 }} />} label={group || 'Invited only'}
                variant="outlined" sx={{ fontSize: '0.7rem', maxWidth: 160 }} />
            )}
            {event.my_rsvp === 'invited' && <Chip size="small" label="Invited" color="secondary" sx={{ fontSize: '0.7rem' }} />}
          </Box>

          {/* Title */}
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.35, mb: 1, color: 'text.primary' }}>
            {event.title}
          </Typography>

          {/* Date */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, color: 'text.secondary' }}>
            <CalendarIcon sx={{ fontSize: 15 }} />
            <Typography variant="body2" fontSize="0.8rem">{formatWhen(event.start_date, event.end_date)}</Typography>
          </Box>

          {/* Location */}
          {(event.location || event.is_online) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, color: 'text.secondary' }}>
              <LocationIcon sx={{ fontSize: 15 }} />
              <Typography variant="body2" fontSize="0.8rem" noWrap>{event.location || 'Online'}</Typography>
            </Box>
          )}

          {/* Organizer */}
          {event.organizer && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, color: 'text.secondary' }}>
              <GroupsIcon sx={{ fontSize: 15 }} />
              <Typography variant="body2" fontSize="0.8rem" noWrap>{event.organizer}</Typography>
            </Box>
          )}

          {/* Description */}
          {event.description && (
            <Typography variant="body2" color="text.secondary" sx={{
              mt: 1, fontSize: '0.8rem', display: '-webkit-box',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {event.description}
            </Typography>
          )}

          {/* Who is going */}
          {(event.going_count > 0 || event.interested_count > 0) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              {event.going_preview?.length > 0 && (
                <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 22, height: 22, fontSize: 11 } }}>
                  {event.going_preview.map((p) => <Avatar key={p.id} src={p.avatar_url || undefined} alt={p.name}>{p.name[0]}</Avatar>)}
                </AvatarGroup>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }} noWrap>
                {line || [
                  event.going_count > 0 && `${event.going_count} going`,
                  event.interested_count > 0 && `${event.interested_count} interested`,
                ].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </CardActionArea>

      <CardActions sx={{ pt: 0, px: 2, pb: 1.5 }}>
        <RsvpButton event={event} onChange={onChange} fullWidth />
      </CardActions>
    </Card>
  );
}

// ── EventsPage ───────────────────────────────────────────────────────────────
export default function EventsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.value === searchParams.get('tab')) ? searchParams.get('tab') : 'discover';
  const [events, setEvents] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState([]);           // several types and areas can be picked
  const [filterStatus, setFilterStatus] = useState('current');
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterOnline, setFilterOnline] = useState('');
  // Only events matching the skills and research interests on your profile (event/relevance.py); your own
  // events and those of your labs, consortia and invitations always show. Hidden without a profile to match.
  const { has_relevance: hasRelevance } = useNewCounts();
  const [onlyRelevantChoice, setOnlyRelevantChoice] = useState(readRelevant);
  const onlyRelevant = onlyRelevantChoice && hasRelevance;
  const toggleRelevant = (on) => {
    setOnlyRelevantChoice(on);
    try { localStorage.setItem(RELEVANT_KEY, on ? '1' : '0'); } catch { /* ignore */ }
  };

  const buildParams = useCallback((pageNo) => {
    const params = new URLSearchParams({ page: pageNo, page_size: PAGE_SIZE });
    Object.entries(TABS.find((t) => t.value === tab).params).forEach(([k, v]) => params.set(k, v));
    if (search)         params.set('search', search);
    if (filterType.length)     params.set('event_type', filterType.join(','));
    if (filterStatus)   params.set('status', filterStatus);
    if (filterCategory.length) params.set('category', filterCategory.join(','));
    if (filterOnline)   params.set('is_online', filterOnline);
    if (onlyRelevant)   params.set('relevant', 'true');
    return params;
  }, [tab, search, filterType, filterStatus, filterCategory, filterOnline, onlyRelevant]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosService.get(`/events/?${buildParams(1)}`);
      setEvents(Array.isArray(data) ? data : data.results ?? []);
      setCount(Array.isArray(data) ? data.length : data.count ?? 0);
      setPage(1);
    } catch {
      setError('Could not load events. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await axiosService.get(`/events/?${buildParams(page + 1)}`);
      setEvents((prev) => [...prev, ...(data.results ?? [])]);
      setPage(page + 1);
    } catch {
      setError('Could not load more events.');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    // research areas only: no countries, Horizon clusters or programme labels
    axiosService.get('/events/research-areas/').then(({ data }) => setCategories(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchEvents, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchEvents, search]);

  const replaceEvent = (updated) => setEvents((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)));

  const clearFilters = () => {
    setFilterType([]);
    setFilterStatus('current');
    setFilterCategory([]);
    setFilterOnline('');
  };

  const hasFilters = search || filterType.length || filterStatus !== 'current' || filterCategory.length || filterOnline;

  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [filterMenuKey, setFilterMenuKey] = useState(null);
  const filters = [
    { key: 'type', label: 'Type', multi: true, value: filterType, set: setFilterType, options: EVENT_TYPES },
    { key: 'when', label: 'When', value: filterStatus, initial: 'current', set: setFilterStatus, options: STATUSES },
    ...(categories.length > 0 ? [{ key: 'area', label: 'Research Area', multi: true, value: filterCategory, set: setFilterCategory,
      options: categories.map((c) => ({ value: c.id, label: c.name })) }] : []),
    { key: 'format', label: 'Format', value: filterOnline, initial: '', set: setFilterOnline,
      options: [{ value: '', label: 'All formats' }, { value: 'true', label: 'Online only' }, { value: 'false', label: 'In-person only' }] },
  ];
  const openFilter = filters.find((f) => f.key === filterMenuKey);
  const isFiltered = (f) => (f.multi ? f.value.length > 0 : f.value !== f.initial);
  const filtersActive = filters.some(isFiltered);
  // the pill names what is picked: "Type: Seminar", "Type (3)"
  const pillLabel = (f) => {
    if (!isFiltered(f)) return f.label;
    const picked = f.options.filter((o) => (f.multi ? f.value.includes(o.value) : o.value === f.value));
    return picked.length === 1 ? `${f.label}: ${picked[0].label}` : `${f.label} (${picked.length})`;
  };
  const toggle = (f, value) => f.set(f.value.includes(value) ? f.value.filter((v) => v !== value) : [...f.value, value]);
  const closeMenu = () => { setFilterMenuAnchor(null); setFilterMenuKey(null); };

  const groupedByStatus = {
    ongoing:   events.filter((e) => e.status === 'ongoing'),
    upcoming:  events.filter((e) => e.status === 'upcoming'),
    past:      events.filter((e) => e.status === 'past'),
    cancelled: events.filter((e) => e.status === 'cancelled'),
  };

  const emptyText = {
    discover:   hasFilters ? 'Try adjusting your filters.'
      : onlyRelevant ? 'Nothing matches the skills and research interests on your profile yet.'
        : 'Be the first to add a research event.',
    going:      'Events you answer Going to appear here.',
    interested: 'Events you mark Interested appear here.',
    invited:    'Nobody has invited you to an event yet.',
    hosting:    'Create a conference, seminar or group meeting and it appears here.',
  }[tab];

  return (
    <PageContainer width="wide">
      {/* tabs after the title; search, calendar and create at the right: the Collaboration Hub's header */}
      <PageHeader title="Events" sx={{ mb: 2 }} actions={(
        <>
          <TextField size="small" placeholder="Search events…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 240 }}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Clear the search" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ) : null,
              },
            }} />
          <Tooltip title="My calendar">
            <IconButton onClick={() => navigate('/events/calendar/')} aria-label="My calendar"
              sx={{ color: '#764ba2', bgcolor: alpha('#764ba2', 0.08), '&:hover': { bgcolor: alpha('#764ba2', 0.16) } }}>
              <CalendarIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap', bgcolor: '#764ba2', '&:hover': { bgcolor: '#5b3680' } }}
          >
            Create
          </Button>
        </>
      )}>
        <Tabs value={tab} onChange={(_, v) => setSearchParams(v === 'discover' ? {} : { tab: v })}
          variant="scrollable" scrollButtons="auto" sx={{ ...HEADER_TABS_SX, minWidth: 0 }}>
          {TABS.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
        </Tabs>
      </PageHeader>

      {/* Filter by: label + pill-button filters, as on the activity feed */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'right', gap: 1, mb: 1, flexWrap: 'wrap' }}>
     
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
          Filter by:
        </Typography>
        {filters.map((f) => {
          const active = isFiltered(f);
          return (
            <Button
              key={f.key}
              onClick={(e) => { setFilterMenuAnchor(e.currentTarget); setFilterMenuKey(f.key); }}
              sx={{
                borderRadius: '20px',
                fontWeight: 500,
                fontSize: '1rem',
                height: 32,
                px: '14px',
                minWidth: 0,
                textTransform: 'none',
                color: active ? 'text.secondary' : 'primary.main',
                bgcolor: (t) => alpha(t.palette.primary.main, active ? 0.12 : 0.07),
                '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, active ? 0.2 : 0.12) },
              }}
            >
              {pillLabel(f)}
            </Button>
          );
        })}
        {hasRelevance && (
          <Tooltip title="Only events matching the skills and research interests on your profile. Your own events, and those of your labs, consortia and invitations, always show.">
            <FormControlLabel sx={{ m: 0, ml: 0.5, whiteSpace: 'nowrap' }}
              control={<Switch size="small" checked={onlyRelevantChoice} onChange={(e) => toggleRelevant(e.target.checked)} />}
              label={<Typography variant="body2">Relevant to me</Typography>} />
          </Tooltip>
        )}
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={closeMenu}
          PaperProps={{ sx: { mt: 0.5, borderRadius: 1.5, minWidth: 160, maxHeight: 420, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' } }}
        >
          {/* several choices: checkboxes, the menu stays open; one choice: radios, picking closes it */}
          {openFilter && openFilter.multi && openFilter.value.length > 0 && (
            <MenuItem onClick={() => openFilter.set([])} sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              Clear selection
            </MenuItem>
          )}
          {openFilter && openFilter.options.map((o) => (
            <MenuItem
              key={o.value || 'all'}
              onClick={() => (openFilter.multi ? toggle(openFilter, o.value) : (openFilter.set(o.value), closeMenu()))}
              sx={{ fontSize: '0.875rem', gap: 0.5 }}
            >
              {openFilter.multi
                ? <Checkbox checked={openFilter.value.includes(o.value)} size="small" sx={{ p: 0 }} />
                : <Radio checked={o.value === openFilter.value} size="small" sx={{ p: 0 }} />}
              {o.label}
            </MenuItem>
          ))}
        </Menu>
        {filtersActive && (
          <Tooltip title="Clear all filters">
            <IconButton size="small" onClick={clearFilters} aria-label="Clear all filters" sx={{ width: 32, height: 32 }}>
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Content */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Grid container spacing={2.5}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <Skeleton variant="rounded" height={240} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      ) : events.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
          <EventIcon sx={{ fontSize: 56, opacity: 0.25, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No events found</Typography>
          <Typography variant="body2" mt={0.5}>{emptyText}</Typography>
          {onlyRelevant && (
            <Button size="small" sx={{ mt: 1.5, textTransform: 'none' }} onClick={() => toggleRelevant(false)}>Show all events</Button>
          )}
        </Box>
      ) : (
        <>
          {groupedByStatus.ongoing.length > 0 && (
            <Section title="Happening Now" color="success.main" events={groupedByStatus.ongoing} onChange={replaceEvent} />
          )}
          {groupedByStatus.upcoming.length > 0 && (
            <Section title="Upcoming" color="#764ba2" events={groupedByStatus.upcoming} onChange={replaceEvent} />
          )}
          {groupedByStatus.past.length > 0 && (
            <Section title="Past Events" color="text.disabled" events={groupedByStatus.past} onChange={replaceEvent} />
          )}
          {groupedByStatus.cancelled.length > 0 && (
            <Section title="Cancelled" color="error.main" events={groupedByStatus.cancelled} onChange={replaceEvent} />
          )}
          {events.length < count && (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button onClick={loadMore} disabled={loadingMore} sx={{ textTransform: 'none' }}>
                {loadingMore ? 'Loading…' : `Show more (${count - events.length})`}
              </Button>
            </Box>
          )}
        </>
      )}

      <EventFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => fetchEvents()}
      />
    </PageContainer>
  );
}

function Section({ title, color, events, onChange }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{ width: 4, height: 20, bgcolor: color, borderRadius: 1, ml:2 }} />
        <Typography variant="subtitle1" fontWeight={700} color={color} >
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">({events.length})</Typography>
      </Box>
      <Grid container spacing={2.5}>
        {events.map((ev) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={ev.id}>
            <EventCard event={ev} onChange={onChange} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
